import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { supabase, getOrganization, getProfile } from '../lib/supabase'

const AuthContext = createContext(null)

// How long to wait for the DB trigger to write the profile row after Google OAuth
const PROFILE_RETRY_MS = 600
const PROFILE_RETRY_MAX = 5

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [profile, setProfile] = useState(null)
  const [org, setOrg]         = useState(null)
  const [loading, setLoading] = useState(true)
  const retryCount = useRef(0)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        loadUserData(session.user.id)
      } else {
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        retryCount.current = 0      // reset retry counter on each new session
        setLoading(true)
        loadUserData(session.user.id)
      } else {
        setProfile(null)
        setOrg(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // ── loadUserData with retry for Google OAuth profile-trigger delay ──────────
  // Supabase's handle_new_user() trigger fires AFTER the OAuth session is
  // created. If we read the profile row before the trigger completes, we get
  // null. We retry up to PROFILE_RETRY_MAX times with PROFILE_RETRY_MS delay.
  async function loadUserData(userId, isRetry = false) {
    try {
      const [{ data: prof }, { data: orgData }] = await Promise.all([
        getProfile(userId),
        getOrganization(userId),
      ])

      if (!prof && retryCount.current < PROFILE_RETRY_MAX) {
        // Profile row not written yet by DB trigger — wait and retry
        retryCount.current++
        setTimeout(() => loadUserData(userId, true), PROFILE_RETRY_MS)
        return
      }

      retryCount.current = 0
      setProfile(prof || null)
      setOrg(orgData || null)
    } catch (e) {
      console.error('loadUserData error:', e)
      if (!isRetry && retryCount.current < PROFILE_RETRY_MAX) {
        retryCount.current++
        setTimeout(() => loadUserData(userId, true), PROFILE_RETRY_MS)
        return
      }
      setProfile(null)
      setOrg(null)
    } finally {
      // Only release loading on final attempt (not intermediate retries)
      if (retryCount.current === 0) setLoading(false)
    }
  }

  async function refreshOrg() {
    if (user) {
      const { data } = await getOrganization(user.id)
      setOrg(data || null)
    }
  }

  const isSuperAdmin = profile?.role === 'superadmin'
  const isLecturer   = profile?.role === 'lecturer'
  const isOrgAdmin   = profile?.role === 'org_admin'

  return (
    <AuthContext.Provider value={{ user, profile, org, loading, refreshOrg, isSuperAdmin, isLecturer, isOrgAdmin }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
